import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type ToastVariant = "success" | "error";

export interface ToastOptions {
  description?: ReactNode;
  durationMs?: number;
  title: ReactNode;
  variant: ToastVariant;
}

export type ToastMessageOptions = Omit<ToastOptions, "title" | "variant">;

export interface ToastApi {
  clearToasts: () => void;
  dismissToast: (id: string) => void;
  error: (title: ReactNode, options?: ToastMessageOptions) => string;
  showToast: (options: ToastOptions) => string;
  success: (title: ReactNode, options?: ToastMessageOptions) => string;
}

export interface ToastProviderProps {
  children: ReactNode;
  viewportLabel?: string;
}

interface ToastRecord extends ToastOptions {
  durationMs: number;
  id: string;
}

const ToastContext = createContext<ToastApi | null>(null);

// UI-LAW does not fix a toast duration; 5s keeps success/error feedback brief.
const defaultToastDurationMs = 5000;

let nextToastId = 0;

function createToastId(): string {
  nextToastId += 1;
  return `toast-${String(nextToastId)}`;
}

export function ToastProvider({
  children,
  viewportLabel = "Notifications",
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);

  const dismissToast = useCallback((id: string) => {
    setToasts((currentToasts) =>
      currentToasts.filter((toast) => toast.id !== id),
    );
  }, []);

  const clearToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const showToast = useCallback((options: ToastOptions) => {
    const toast: ToastRecord = {
      ...options,
      durationMs: Math.max(1, options.durationMs ?? defaultToastDurationMs),
      id: createToastId(),
    };

    setToasts((currentToasts) => [...currentToasts, toast]);
    return toast.id;
  }, []);

  // The hook surface: show/dismiss/clear plus success/error helpers.
  const value = useMemo<ToastApi>(
    () => ({
      clearToasts,
      dismissToast,
      error: (title, options = {}) =>
        showToast({ ...options, title, variant: "error" }),
      showToast,
      success: (title, options = {}) =>
        showToast({ ...options, title, variant: "success" }),
    }),
    [clearToasts, dismissToast, showToast],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport
        label={viewportLabel}
        onDismiss={dismissToast}
        toasts={toasts}
      />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }

  return context;
}

// For leaf primitives that must render anywhere — inside the app shell, in
// Storybook, in a bare test — and merely *prefer* a toast. A screen owning a
// task uses `useToast` and should fail loudly when unwrapped; a primitive like
// `Copyable` degrades to local feedback instead.
export function useOptionalToast(): ToastApi | null {
  return useContext(ToastContext);
}

interface ToastViewportProps {
  label: string;
  onDismiss: (id: string) => void;
  toasts: ToastRecord[];
}

function ToastViewport({ label, onDismiss, toasts }: ToastViewportProps) {
  if (toasts.length === 0) {
    return null;
  }

  return (
    <div aria-label={label} className="marker-toast-viewport" role="region">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} onDismiss={onDismiss} toast={toast} />
      ))}
    </div>
  );
}

interface ToastItemProps {
  onDismiss: (id: string) => void;
  toast: ToastRecord;
}

function ToastItem({ onDismiss, toast }: ToastItemProps) {
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      onDismiss(toast.id);
    }, toast.durationMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [onDismiss, toast.durationMs, toast.id]);

  const classes = ["marker-toast", `marker-toast--${toast.variant}`];
  const label = toast.variant === "success" ? "Success" : "Error";
  const dismissLabel =
    typeof toast.title === "string"
      ? `Dismiss ${toast.title}`
      : "Dismiss notification";

  return (
    <article
      aria-atomic="true"
      className={classes.join(" ")}
      role={toast.variant === "error" ? "alert" : "status"}
    >
      <div className="marker-toast__content">
        <p
          className={[
            "marker-toast__label",
            `marker-toast__label--${toast.variant}`,
          ].join(" ")}
        >
          {label}
        </p>
        <p className="marker-toast__title">{toast.title}</p>
        {toast.description ? (
          <p className="marker-toast__description">{toast.description}</p>
        ) : null}
      </div>
      <button
        aria-label={dismissLabel}
        className="marker-toast__dismiss"
        onClick={() => {
          onDismiss(toast.id);
        }}
        type="button"
      >
        x
      </button>
    </article>
  );
}
